<template>
  <DragHandle
    class="drag-handle"
    :editor="editor"
    @nodeChange="handleNodeChange"
    pluginKey="drag-handle"
  >
    <div class="flex gap-1">
      <button class="drag-handle__button" @click="toggleMenu" ref="menuTarget">
        <i class="fa-solid fa-plus" />
      </button>
      <button class="drag-handle__button">
        <i class="fa-solid fa-grip-vertical" />
      </button>
    </div>
    <Menu
      class="drag-handle__menu"
      ref="menu"
      :model="blockOptions"
      :popup="true"
      @show="onMenuShow"
      @hide="onMenuHide"
    />
  </DragHandle>
</template>

<script setup lang="ts">
  import {
    DragHandle,
    type DragHandleProps,
  } from "@tiptap-pro/extension-drag-handle-vue-3";
  import { Editor } from "@tiptap/core";
  import { Node } from "@tiptap/pm/model";
  import Menu from "primevue/menu";

  const props = defineProps<{
    editor: Editor;
  }>();

  const menu = ref();
  const menuTarget = ref();

  const blockOptions = [
    {
      icon: "fa-solid fa-image",
      label: "Image",
      command: () => toggleBlock("image"),
    },
  ];

  const toggleMenu = (event: Event) => {
    menu.value.toggle(event);
  };

  const onMenuShow = () => {
    props.editor.commands.lockDragHandle();
  };

  const onMenuHide = () => {
    props.editor.commands.unlockDragHandle();
  };

  const dragHandleNode = ref<Node | null>(null);
  const dragHandlePos = ref<number | null>(null);
  const handleNodeChange: DragHandleProps["onNodeChange"] = (data) => {
    dragHandleNode.value = data.node as Node;
    dragHandlePos.value = data.pos;
  };

  const toggleBlock = (type: string, attrs = {}) => {
    const pos = dragHandlePos.value;
    const node = dragHandleNode.value;

    if (pos === null || pos < 0 || !node) return;

    const currentNodeSize = node.nodeSize;
    const insertPos = pos + currentNodeSize;
    const currentNodeIsEmptyParagraph =
      node.type.name === "paragraph" && node.content?.size === 0;
    const focusPos = currentNodeIsEmptyParagraph ? pos + 1 : insertPos + 1;

    props.editor
      .chain()
      .command(({ dispatch, tr, state }) => {
        if (dispatch) {
          if (currentNodeIsEmptyParagraph) {
            tr.setNodeMarkup(pos, state.schema.nodes[type], attrs);
          } else {
            tr.insert(insertPos, state.schema.nodes[type]!.create(attrs));
          }

          return dispatch(tr);
        }

        return true;
      })
      .focus(focusPos)
      .run();
  };
</script>

<style lang="scss" scoped>
  .drag-handle {
    &__button {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 24px;
      aspect-ratio: 1/1;

      font-size: 12px;
      font-weight: 700;

      cursor: grab;

      background: #0d0d0d10;
      color: #0d0d0d80;

      border-radius: 100%;
    }
  }
</style>
